import { useState } from 'react';
import { BarChart3, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import type { MeetingQualityData, ParticipantQosData } from './overview-types';
import { isNotAvailable } from './overview-types';
import { QosDetailPanel } from './QosDetailPanel';

interface Props {
  data: MeetingQualityData;
  accountId?: string;
}

export function MeetingQualitySection({ data, accountId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<string | null>(null);
  const [qosData, setQosData] = useState<ParticipantQosData | null>(null);
  const [qosLoading, setQosLoading] = useState(false);

  const { qualityDistribution: dist } = data;
  const total = dist.good + dist.fair + dist.poor + dist.bad;

  async function handleMeetingClick(uuid: string) {
    if (selectedMeeting === uuid) {
      setSelectedMeeting(null);
      setQosData(null);
      return;
    }

    setSelectedMeeting(uuid);
    setQosLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (accountId) params.set('accountId', accountId);
      const res = await fetch(
        `${API_BASE}/zoom/meetings/${encodeURIComponent(uuid)}/qos?${params}`,
        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
      );
      if (res.ok) {
        const result = await res.json();
        if (!isNotAvailable(result)) {
          setQosData(result as ParticipantQosData);
        }
      }
    } catch {
      // QoS fetch failed silently
    } finally {
      setQosLoading(false);
    }
  }

  return (
    <div className="card overflow-hidden">
      <div
        className="flex items-center gap-3 border-b border-border px-5 py-4 cursor-pointer hover:bg-secondary/50"
        onClick={() => setExpanded(!expanded)}
      >
        <BarChart3 className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Meeting Quality</h3>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-500">
          {data._scope}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </div>

      {/* Summary (always visible) */}
      <div className="px-5 py-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <QualityChip label="Good" count={dist.good} total={total} color="green" />
          <QualityChip label="Fair" count={dist.fair} total={total} color="yellow" />
          <QualityChip label="Poor" count={dist.poor} total={total} color="orange" />
          <QualityChip label="Bad" count={dist.bad} total={total} color="red" />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {data.totalMeetings} meetings analyzed (last 30 days)
        </p>
      </div>

      {/* Expanded meeting list */}
      {expanded && data.recentMeetings.length > 0 && (
        <div className="border-t border-border px-5 py-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent Meetings</p>
          <div className="space-y-1">
            {data.recentMeetings.map((m) => (
              <div key={m.uuid}>
                <button
                  onClick={() => handleMeetingClick(m.uuid)}
                  className="w-full flex items-center justify-between py-2 px-2 rounded hover:bg-secondary/50 text-left transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-foreground truncate block">{m.topic}</span>
                    <span className="text-xs text-muted-foreground">
                      {m.startTime ? new Date(m.startTime).toLocaleDateString() : '—'} · {m.participants} participants · {m.duration}min
                    </span>
                  </div>
                  <QualityBadge quality={m.quality} />
                </button>

                {selectedMeeting === m.uuid && (
                  <div className="ml-4 mt-1 mb-2">
                    {qosLoading ? (
                      <div className="flex items-center gap-2 py-3 text-muted-foreground text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading QoS data...
                      </div>
                    ) : qosData ? (
                      <QosDetailPanel data={qosData} />
                    ) : (
                      <p className="text-xs text-muted-foreground py-2">QoS data not available for this meeting</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QualityChip({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const colorMap: Record<string, string> = {
    green: 'bg-green-500/10 text-green-500',
    yellow: 'bg-yellow-500/10 text-yellow-500',
    orange: 'bg-orange-500/10 text-orange-500',
    red: 'bg-red-500/10 text-red-500',
  };

  return (
    <div className="rounded-lg border border-border/50 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold text-foreground">{count}</p>
      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${colorMap[color] || colorMap.green}`}>
        {pct}%
      </span>
    </div>
  );
}

function QualityBadge({ quality }: { quality: string }) {
  const styles: Record<string, string> = {
    good: 'bg-green-500/10 text-green-500',
    fair: 'bg-yellow-500/10 text-yellow-500',
    poor: 'bg-orange-500/10 text-orange-500',
    bad: 'bg-red-500/10 text-red-500',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[quality] || styles.good}`}>
      {quality}
    </span>
  );
}
